// Loads the attribution and signup-tracking code that the app needs only after its startup: the per-ad
// attribution helpers (acquisition-attribution.ts), the Meta Conversions API helpers
// (backend/utils/meta-conversion-tracking.ts), the post-signup tracking (backend/utils/new-user-signup-tracking.ts)
// and the mobile app's install-attribution bridge (backend/utils/install-attribution-bridge.ts). They stay out of
// the startup JavaScript (scripts/checkStartupBudget.mjs); startup code imports this module, never those (types
// excepted).
//
// They are downloaded right after startup rather than on first use (preloadModulesUsedAfterStartup), so they
// always come from the same deployment as the page: a chunk that a later deployment removed would make the
// first use fail and reload the page (utils/vite-preload-error-recovery.ts) in the middle of a signup or checkout.

import { createModuleLoader } from "./create-module-loader";

export const loadAcquisitionAttributionModule = createModuleLoader(() => import("./acquisition-attribution"));
export const loadMetaConversionTrackingModule = createModuleLoader(
  () => import("../backend/utils/meta-conversion-tracking")
);
export const loadNewUserSignupTrackingModule = createModuleLoader(
  () => import("../backend/utils/new-user-signup-tracking")
);
export const loadInstallAttributionBridgeModule = createModuleLoader(
  () => import("../backend/utils/install-attribution-bridge")
);

// Settles once this page's arrival touch is stored (or its capture failed).
let arrivalCapturePromise: Promise<void> = Promise.resolve();

/**
 * At app startup: records the campaign on the arrival URL and, in the mobile app, its install attribution, then
 * downloads the other modules above. The URL, referrer and injected install attribution are read now,
 * synchronously, because the router can rewrite the URL before the helpers arrive.
 */
export function captureAcquisitionAttributionOnAppArrival(): void {
  if (typeof window === "undefined") return;
  const appArrival = {
    search: window.location.search,
    pathname: window.location.pathname,
    referrer: typeof document !== "undefined" ? document.referrer : null,
    nativeInstallAttribution: window.__JUNGLE_NATIVE_INSTALL_ATTRIBUTION__,
  };
  arrivalCapturePromise = loadAcquisitionAttributionModule()
    .then((acquisitionAttribution) => acquisitionAttribution.captureAcquisitionAttributionAtAppStartup(appArrival))
    .catch(() => {
      // Attribution is best effort; it must never break startup.
    });
  for (const loadModule of [loadMetaConversionTrackingModule, loadNewUserSignupTrackingModule]) {
    void loadModule().catch(() => {});
  }
  if (window.ReactNativeWebView) void loadInstallAttributionBridgeModule().catch(() => {});
}

/**
 * The attribution helpers once this page's arrival touch is stored, so a profile created now reads it
 * (createUserOnFirebase) and an account-boundary clear cannot run before it.
 */
export async function loadAcquisitionAttributionAfterArrivalCapture() {
  await arrivalCapturePromise;
  return loadAcquisitionAttributionModule();
}

/**
 * Account boundary (AuthContext, when a signed-in account's profile loads): the touches this browser recorded
 * so far belong to that account, so a later sign-up here, after a sign-out, does not inherit them.
 */
export function clearAcquisitionTouchesForSignedInAccount(): void {
  void loadAcquisitionAttributionAfterArrivalCapture()
    .then((acquisitionAttribution) => acquisitionAttribution.clearAcquisitionTouchesAtAccountSignIn())
    .catch(() => {
      // Best effort, as above.
    });
}
